import React from 'react';
import {
  KamBamTitle,
  ColumnCount,
  LoadMoreButton,
} from './styles';

import {
  Card,
  ControlledBoard,
  KanbanBoard,
  moveCard,
  OnDragEndNotification,
} from '@caldwell619/react-kanban';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { normalizePath } from 'utils/convert';
import { PersonalClientCRUD } from 'services/requests/personal-client';
import { CONST_PERSONAL_CLIENT_KAMBAM_STEP } from 'utils/CONSTANTS';

import styled  from 'styled-components';
import CardUserJob from 'components/cards/user-job';
import '@caldwell619/react-kanban/dist/styles.css';

import typ from './index.d'

const LOAD_MORE_ID_PREFIX = '__load_more__';

const personalClientCrud = new PersonalClientCRUD();

const KanBan: React.FC<typ.Props> = ({ board, setCurrentClient, setShowModal, loadMoreForColumn, columnMeta }) => {
  const navigation = useNavigate();

  const [controlledBoard, setBoard] = React.useState<KanbanBoard<any>>(board);
  const [savingCardId, setSavingCardId] = React.useState<number | null>(null);
  const [loadingColumn, setLoadingColumn] = React.useState<number | null>(null);

  // Build board with "load more" placeholder cards
  const buildBoardWithLoadMore = React.useCallback((baseBoard: KanbanBoard<any>, meta?: Map<number, { hasMore: boolean; currentPage: number; total: number }>) => {
    if (!meta) return baseBoard;

    const newBoard = JSON.parse(JSON.stringify(baseBoard));
    newBoard.columns.forEach((col: any, index: number) => {
      // Remove existing load-more placeholders
      col.cards = col.cards.filter((c: any) => !String(c.id).startsWith(LOAD_MORE_ID_PREFIX));

      const colMeta = meta.get(index);
      if (colMeta?.hasMore) {
        col.cards.push({
          id: `${LOAD_MORE_ID_PREFIX}${index}`,
          __isLoadMore: true,
          __columnIndex: index,
        });
      }
    });
    return newBoard;
  }, []);

  // Sync board when props change
  React.useEffect(() => {
    setBoard(buildBoardWithLoadMore(board, columnMeta));
  }, [board, columnMeta, buildBoardWithLoadMore]);

  // Drag with error recovery
  const handleCardMove: OnDragEndNotification<Card> = async (
    _card,
    source,
    destination,
  ) => {
    // Prevent dragging load-more placeholder
    if (String(_card.id).startsWith(LOAD_MORE_ID_PREFIX)) return;

    const previousBoard = controlledBoard;
    setBoard(currentBoard => moveCard(currentBoard, source, destination));

    setSavingCardId(Number(_card.id));
    const success = await handleUpdateKambamStep(Number(_card.id), Number(destination?.toColumnId));
    if (!success) {
      setBoard(previousBoard);
    }
    setSavingCardId(null);
  };

  const handleUpdateKambamStep = React.useCallback(
    async (idCard: number, columnId: number): Promise<boolean> => {
      const step = CONST_PERSONAL_CLIENT_KAMBAM_STEP[columnId];
      if (!step) return false;

      const currentKambam = step.keys;

      try {
        const {
          data,
          status: Status,
          error,
        } = await personalClientCrud.update({
          id: idCard,
          kanban_step: currentKambam,
        });

        if (error) throw new Error(error.message.description);
        if (Status === 200 && data) {
          toast('Passo no Kambam atualizado com sucesso.');
          return true;
        }
        return false;
      } catch (e) {
        toast.error('Erro ao atualizar o Kambam. Revertendo...');
        return false;
      }
    },
    [],
  );

  const handleLoadMore = React.useCallback(async (columnIndex: number) => {
    if (!loadMoreForColumn) return;
    setLoadingColumn(columnIndex);
    await loadMoreForColumn(columnIndex);
    setLoadingColumn(null);
  }, [loadMoreForColumn]);


  return (
     <KambamStyles>
      <ControlledBoard
        disableColumnDrag
        allowAddCard={false}
        allowAddColumn={false}
        allowRenameColumn={false}
        onCardDragEnd={handleCardMove}
        allowRemoveColumn={false}
        allowRemoveCard={false}
        renderColumnHeader={(e) => {
          const meta = columnMeta?.get(Number(e.id));
          const totalCount = meta?.total ?? e.cards.filter((c: any) => !String(c.id).startsWith(LOAD_MORE_ID_PREFIX)).length;

          return (
            <KamBamTitle
              id={Number(e.id)}
              onClick={() => navigation(`/recolocacao-profissional/${normalizePath(e.title)}`, {
                state: {
                  kanban_step: e.title
                }
              }
            )}>
              {e.title}
              <ColumnCount>{totalCount}</ColumnCount>
            </KamBamTitle>
          )
        }}
        renderCard={(card: any) => {
          // Render "load more" placeholder
          if (card.__isLoadMore) {
            const colIdx = card.__columnIndex;
            return (
              <LoadMoreButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleLoadMore(colIdx);
                }}
                disabled={loadingColumn === colIdx}
              >
                {loadingColumn === colIdx ? 'Carregando...' : 'Carregar mais'}
              </LoadMoreButton>
            );
          }

          return (
            <CardUserJob
              onClick={() => {
                setCurrentClient(card);
                setShowModal(true);
              }}
              compact
              style={savingCardId === card.id ? { opacity: 0.6, pointerEvents: 'none' as const } : undefined}
              client={{
                advisor_relation: card.advisor_relation,
                budget: card.budget,
                client_type: card.client_type,
                cpf: card.cpf,
                id: card.id,
                image: card.image,
                kanban_step: card.kanban_step,
                local: card.local,
                name: card.name,
                occupation: card.occupation,
                status: card.status,
                vacancy_matching_quantity: card.vacancy_matching_quantity,
                origin: card.origin,
              }}
            />
          );
        }}>
        {controlledBoard}
      </ControlledBoard>
    </KambamStyles>
  )
};

export default KanBan;

const KambamStyles = styled('div')`
  flex: 1;
  display: flex;
  & h1,
  h2,
  h3,
  h4,
  h5,
  & .react-kanban-column {
    padding: 0;
    background-color: transparent;
    margin-right: 30px;

  }
  & .react-kanban-column-header {
    border-radius: 5px;
    padding: 12px;
    margin-bottom: 25px;
    & span {
      color: white;
    }
  }
  & .react-kanban-card {
    margin-bottom: 20px;
    max-width: unset;
    min-width: unset;
    width: auto;
    padding: 0;
    border: none;
    box-shadow: none;
    background: transparent;
  }
`;
